"use client";

import React from "react";
import { useTranslations } from "next-intl";
import Breadcrumb from "@/components/Common/Breadcrumb";
import { getImagePath } from "@/lib/utils";
import { useLocale } from "next-intl";
import { formatNumberByLocale } from "@/utils/numberFormatter";

interface SubSection {
  subTitle: string;
  desc?: string;
  points?: string[];
  callout?: string;
  desc2?: string;
}

interface Term {
  title: string;
  desc?: string;
  points?: string[];
  callout?: string;
  desc2?: string;
  subSections?: SubSection[];
}

const TermsOfUse = () => {
  const t = useTranslations("termsofuse");
  const locale = useLocale();

  // Keys that only some languages have (Own & Earn update in en/mr/hi):
  // returns [text] when the key exists, [] otherwise.
  const opt = (key: string) => (t.has(key) ? [t(key)] : []);

  const terms: Term[] = [
    // 1. Definitions
    {
      title: t("title_1"),
      desc: t("desc_1"),
      points: [t("point_1_1"), t("point_1_2"), t("point_1_3"), t("point_1_4"), t("point_1_5"), t("point_1_6"), t("point_1_7"), t("point_1_8"), t("point_1_9"), t("point_1_10"), t("point_1_11"), t("point_1_12"), t("point_1_13"), ...opt("point_1_14")],
    },
    // 2. Eligibility
    {
      title: t("title_2"),
      points: [t("point_2_1"), t("point_2_2"), t("point_2_3"), t("point_2_4")],
    },
    // 3. Pay-Per-View (PPV) Service
    {
      title: t("title_3"),
      subSections: [
        { subTitle: t("sub_3_1_title"), desc: t("sub_3_1_desc") },
        { subTitle: t("sub_3_2_title"), points: [t("sub_3_2_point_1"), t("sub_3_2_point_2"), t("sub_3_2_point_3"), t("sub_3_2_point_4")] },
        { subTitle: t("sub_3_3_title"), points: [t("sub_3_3_point_1"), t("sub_3_3_point_2"), t("sub_3_3_point_3"), t("sub_3_3_point_4")] },
        { subTitle: t("sub_3_4_title"), points: [t("sub_3_4_point_1"), t("sub_3_4_point_2"), t("sub_3_4_point_3")] },
        { subTitle: t("sub_3_5_title"), desc: t("sub_3_5_desc") },
        { subTitle: t("sub_3_6_title"), desc: t("sub_3_6_desc") },
      ],
    },
    // 4. KST Own & Earn (Village Partner Program in languages not yet updated)
    {
      title: t("title_4"),
      subSections: [
        { subTitle: t("sub_4_1_title"), desc: t("sub_4_1_desc") },
        { subTitle: t("sub_4_2_title"), points: [t("sub_4_2_point_1"), t("sub_4_2_point_2"), t("sub_4_2_point_3"), t("sub_4_2_point_4")] },
        { subTitle: t("sub_4_3_title"), points: [t("sub_4_3_point_1"), t("sub_4_3_point_2"), t("sub_4_3_point_3")] },
        { subTitle: t("sub_4_4_title"), points: [t("sub_4_4_point_1"), t("sub_4_4_point_2"), t("sub_4_4_point_3"), t("sub_4_4_point_4"), t("sub_4_4_point_5")] },
        { subTitle: t("sub_4_5_title"), desc: t("sub_4_5_desc") },
      ],
    },
    // 5. Producer / Content Partner Terms
    {
      title: t("title_5"),
      subSections: [
        { subTitle: t("sub_5_1_title"), desc: t("sub_5_1_desc") },
        { subTitle: t("sub_5_2_title"), points: [t("sub_5_2_point_1"), t("sub_5_2_point_2")] },
        { subTitle: t("sub_5_3_title"), desc: t("sub_5_3_desc") },
        { subTitle: t("sub_5_4_title"), desc: t("sub_5_4_desc") },
        { subTitle: t("sub_5_5_title"), desc: t("sub_5_5_desc"), points: [t("sub_5_5_point_1"), t("sub_5_5_point_2"), t("sub_5_5_point_3"), t("sub_5_5_point_4"), t("sub_5_5_point_5"), t("sub_5_5_point_6")] },
      ],
    },
    // 6. KST Stick & Device Terms
    {
      title: t("title_6"),
      subSections: [
        { subTitle: t("sub_6_1_title"), desc: t("sub_6_1_desc") },
        { subTitle: t("sub_6_2_title"), desc: t("sub_6_2_desc"), points: [t("sub_6_2_point_1"), t("sub_6_2_point_2"), t("sub_6_2_point_3"), t("sub_6_2_point_4")] },
        { subTitle: t("sub_6_3_title"), points: [t("sub_6_3_point_1"), t("sub_6_3_point_2"), t("sub_6_3_point_3")] },
        { subTitle: t("sub_6_4_title"), desc: t("sub_6_4_desc"), points: [t("sub_6_4_point_1"), t("sub_6_4_point_2"), t("sub_6_4_point_3"), t("sub_6_4_point_4")], callout: t("sub_6_4_callout") },
        { subTitle: t("sub_6_5_title"), desc: t("sub_6_5_desc"), points: [t("sub_6_5_point_1"), t("sub_6_5_point_2"), t("sub_6_5_point_3"), t("sub_6_5_point_4"), t("sub_6_5_point_5")] },
      ],
    },
    // 7. GPS Location and Device Data Collection
    {
      title: t("title_7"),
      subSections: [
        { subTitle: t("sub_7_1_title"), desc: t("sub_7_1_desc"), points: [t("sub_7_1_point_1"), t("sub_7_1_point_2"), t("sub_7_1_point_3")], desc2: t("sub_7_1_desc2") },
        { subTitle: t("sub_7_1b_title"), points: [t("sub_7_1b_point_1"), t("sub_7_1b_point_2"), t("sub_7_1b_point_3"), t("sub_7_1b_point_4"), t("sub_7_1b_point_5")] },
        { subTitle: t("sub_7_2_title"), points: [t("sub_7_2_point_1"), t("sub_7_2_point_2"), t("sub_7_2_point_3")] },
        { subTitle: t("sub_7_3_title"), desc: t("sub_7_3_desc"), points: [t("sub_7_3_point_1"), t("sub_7_3_point_2"), t("sub_7_3_point_3"), t("sub_7_3_point_4")], desc2: t("sub_7_3_desc2") },
      ],
    },
    // 8. Payment, Refunds, and Cancellation
    {
      title: t("title_8"),
      subSections: [
        { subTitle: t("sub_8_1_title"), desc: t("sub_8_1_desc"), points: [t("sub_8_1_point_1"), t("sub_8_1_point_2"), t("sub_8_1_point_3"), t("sub_8_1_point_4"), ...opt("sub_8_1_point_5")] },
        { subTitle: t("sub_8_2_title"), desc: t("sub_8_2_desc") },
        { subTitle: t("sub_8_3_title"), desc: t("sub_8_3_desc") },
        { subTitle: t("sub_8_4_title"), desc: t("sub_8_4_desc"), points: [t("sub_8_4_point_1"), t("sub_8_4_point_2"), t("sub_8_4_point_3")], desc2: t("sub_8_4_desc2") },
        ...(t.has("sub_8_5_title") ? [{ subTitle: t("sub_8_5_title"), desc: t("sub_8_5_desc") }] : []),
      ],
    },
    // 9. Registration and User Accounts
    {
      title: t("title_9"),
      points: [t("point_9_1"), t("point_9_2"), t("point_9_3"), t("point_9_4")],
    },
    // 10. Producer Video Call Feature
    {
      title: t("title_10"),
      subSections: [
        { subTitle: t("sub_10_1_title"), desc: t("sub_10_1_desc") },
        { subTitle: t("sub_10_2_title"), points: [t("sub_10_2_point_1"), t("sub_10_2_point_2"), t("sub_10_2_point_3"), t("sub_10_2_point_4"), t("sub_10_2_point_5")] },
        { subTitle: t("sub_10_3_title"), points: [t("sub_10_3_point_1"), t("sub_10_3_point_2"), t("sub_10_3_point_3")] },
      ],
    },
    // 11. Family Safe Mode
    {
      title: t("title_11"),
      points: [t("point_11_1"), t("point_11_2"), t("point_11_3"), t("point_11_4")],
    },
    // 12. Content Standards and Complaints
    {
      title: t("title_12"),
      subSections: [
        { subTitle: t("sub_12_1_title"), desc: t("sub_12_1_desc"), points: [t("sub_12_1_point_1"), t("sub_12_1_point_2"), t("sub_12_1_point_3"), t("sub_12_1_point_4"), t("sub_12_1_point_5"), t("sub_12_1_point_6"), t("sub_12_1_point_7"), t("sub_12_1_point_8")] },
        { subTitle: t("sub_12_2_title"), desc: t("sub_12_2_desc"), points: [t("sub_12_2_point_1"), t("sub_12_2_point_2")], desc2: t("sub_12_2_desc2") },
        { subTitle: t("sub_12_3_title"), points: [t("sub_12_3_point_1"), t("sub_12_3_point_2"), t("sub_12_3_point_3"), t("sub_12_3_point_4")] },
      ],
    },
    // 13. DRM Protection and Anti-Piracy
    {
      title: t("title_13"),
      subSections: [
        { subTitle: t("sub_13_1_title"), desc: t("sub_13_1_desc") },
        { subTitle: t("sub_13_2_title"), desc: t("sub_13_2_desc"), points: [t("sub_13_2_point_1"), t("sub_13_2_point_2"), t("sub_13_2_point_3"), t("sub_13_2_point_4"), t("sub_13_2_point_5"), t("sub_13_2_point_6")], callout: t("sub_13_2_callout") },
        { subTitle: t("sub_13_3_title"), desc: t("sub_13_3_desc") },
      ],
    },
    // 14. Intellectual Property and Ownership
    {
      title: t("title_14"),
      points: [t("point_14_1"), t("point_14_2"), t("point_14_3"), t("point_14_4")],
    },
    // 15. User Restrictions
    {
      title: t("title_15"),
      desc: t("desc_15"),
      points: [t("point_15_1"), t("point_15_2"), t("point_15_3"), t("point_15_4"), t("point_15_5"), t("point_15_6"), t("point_15_7"), t("point_15_8")],
    },
    // 16. Termination
    {
      title: t("title_16"),
      points: [t("point_16_1"), t("point_16_2"), t("point_16_3")],
    },
    // 17. Disclaimer of Warranties
    { title: t("title_17"), desc: t("desc_17") },
    // 18. Limitation of Liability
    { title: t("title_18"), desc: t("desc_18") },
    // 19. Indemnification
    { title: t("title_19"), desc: t("desc_19") },
    // 20. Privacy Policy
    { title: t("title_20"), desc: t("desc_20") },
    // 21. Governing Law and Dispute Resolution
    {
      title: t("title_21"),
      points: [t("point_21_1"), t("point_21_2"), t("point_21_3"), t("point_21_4")],
    },
    // 22. Amendments
    { title: t("title_22"), desc: t("desc_22") },
    // 23. Miscellaneous
    {
      title: t("title_23"),
      points: [t("point_23_1"), t("point_23_2"), t("point_23_3"), t("point_23_4"), t("point_23_5"), t("point_23_6")],
    },
    // 24. Contact Us
    {
      title: t("title_24"),
      desc: t("desc_24"),
      points: [t("point_24_1"), t("point_24_2"), t("point_24_3"), t("point_24_4"), t("point_24_5")],
    },
  ];

  return (
    <section className="min-h-screen bg-gray-50 py-24">
      <Breadcrumb
        pageName={t("pageName")}
        description=""
        bgImages={[getImagePath("/images/breadcrumb/howItWorks.webp")]}
        bgIndex={0}
      />

      <div className="container mx-auto px-4 md:px-6 py-10">
        <p className="mb-2 text-sm text-gray-500">{t("lastUpdated")}</p>

        <p className="mb-4">{t("introText")}</p>

        <div className="bg-amber-50 border-l-4 border-amber-400 p-4 mb-8 text-sm text-gray-700 rounded-r font-medium">
          {t("cautionText")}
        </div>

        <div className="space-y-6">
          {terms.map((term, idx) => (
            <div key={idx} className="flex items-start gap-3">
              <span className="text-lg font-bold">
                {formatNumberByLocale(locale, idx + 1)}.
              </span>

              <div className="flex-1">
                <strong className="block mb-1">{term.title}</strong>

                {term.desc && <p className="mb-2">{term.desc}</p>}

                {term.points && term.points.length > 0 && (
                  <ul className="list-disc pl-4 mb-2">
                    {term.points.map((point, i) => (
                      <li key={i}>{point}</li>
                    ))}
                  </ul>
                )}

                {term.callout && (
                  <div className="bg-amber-50 border-l-4 border-amber-400 p-3 my-2 text-sm text-gray-700 rounded-r">
                    {term.callout}
                  </div>
                )}

                {term.desc2 && <p className="mt-2">{term.desc2}</p>}

                {/* Sub-sections */}
                {term.subSections && term.subSections.length > 0 && (
                  <div className="mt-3 space-y-4">
                    {term.subSections.map((sub, si) => (
                      <div key={si} className="pl-2">
                        <strong className="block mb-1 text-[15px]">{sub.subTitle}</strong>

                        {sub.desc && <p className="mb-2">{sub.desc}</p>}

                        {sub.points && sub.points.length > 0 && (
                          <ul className="list-disc pl-4 mb-2">
                            {sub.points.map((point, pi) => (
                              <li key={pi}>{point}</li>
                            ))}
                          </ul>
                        )}

                        {sub.callout && (
                          <div className="bg-amber-50 border-l-4 border-amber-400 p-3 my-2 text-sm text-gray-700 rounded-r">
                            {sub.callout}
                          </div>
                        )}

                        {sub.desc2 && <p className="mt-2">{sub.desc2}</p>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        <p className="mt-8 italic text-gray-600">
          {t("disclaimer")}
        </p>
      </div>
    </section>
  );
};

export default TermsOfUse;
